import React, { useState, useRef, useEffect } from 'react';
import './LanguageSelector.css';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * LanguageSelector component for switching between German and English
 * Accessible dropdown menu with keyboard navigation support
 */
const LanguageSelector = () => {
  const { t, setLanguage, isGerman } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const handleButtonClick = () => {
    setIsMenuOpen(prev => !prev);
  };

  const handleLanguageSelect = (lang) => {
    setLanguage(lang);
    setIsMenuOpen(false);
    buttonRef.current?.focus();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setIsMenuOpen(false);
      buttonRef.current?.focus();
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !buttonRef.current?.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMenuOpen]);

  // Show the CURRENT language
  const currentLanguageLabel = isGerman ? 'Deutsch' : 'English';

  return (
    <div className="language-selector-wrapper" onKeyDown={handleKeyDown}>
      <button
        ref={buttonRef}
        className={`language-selector ${isMenuOpen ? 'language-selector--open' : ''}`}
        onClick={handleButtonClick}
        aria-label={t('a11y.languageSelectLabel')}
        aria-expanded={isMenuOpen}
        aria-haspopup="listbox"
        aria-controls="language-menu"
        type="button"
      >
        <svg
          className="language-selector-icon"
          width="19"
          height="19"
          viewBox="0 0 19 19"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle cx="9.5" cy="9.5" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M1 9.5H18" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M9.5 1C11.5 4 12 7 12 9.5C12 12 11.5 15 9.5 18" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M9.5 1C7.5 4 7 7 7 9.5C7 12 7.5 15 9.5 18" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
        <span className="language-selector-text">{currentLanguageLabel}</span>
        <svg
          className="language-selector-arrow"
          width="12"
          height="6"
          viewBox="0 0 12 6"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path d="M1 1L6 5L11 1" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      </button>

      {/* Language Dropdown Menu */}
      {isMenuOpen && (
        <ul
          ref={menuRef}
          id="language-menu"
          className="language-selector-menu"
          role="listbox"
          aria-label={t('a11y.languageSelectLabel')}
        >
          <li role="option" aria-selected={isGerman}>
            <button
              className={`language-selector-option ${isGerman ? 'language-selector-option--selected' : ''}`}
              onClick={() => handleLanguageSelect('de')}
              type="button"
            >
              Deutsch
            </button>
          </li>
          <li role="option" aria-selected={!isGerman}>
            <button
              className={`language-selector-option ${!isGerman ? 'language-selector-option--selected' : ''}`}
              onClick={() => handleLanguageSelect('en')}
              type="button"
            >
              English
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

export default LanguageSelector;
